<template>
  <div
    id="counting-numbers"
    ref="countingSection"
    class="counting-numbers-section"
  >
    <div class="container">
      <div
        class="banner-text"
        v-html="section.title"
      />
      <div class="counting-numbers">
        <div
          v-for="(number_info_block, index) of section.number_info_blocks"
          :key="number_info_block.date"
          class="blocks"
        >
          <div
            class="title"
            v-html="number_info_block.title"
          />
          <div
            class="content"
            v-html="number_info_block.content"
          />
          <p
            class="number-header"
            v-html="number_info_block.number_header"
          />
          <p
            class="number"
            v-text="formattedNumber(index)"
          />
          <p
            class="grants"
            v-text="number_info_block.grants"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import stripBlockTags from '@/utils/strip-block-tags';

export default {
  name: 'BannerLikeSection',

  props: {
    section: { type: Object, required: true }
  },

  data() {
    return {
      animatedNumbers: [], // Array to hold current animated values for each block
      animationDuration: 2000, // Duration in ms (adjust as needed, e.g., 2000 for 2 seconds)
      hasAnimated: false // Flag to ensure animation only runs once
    };
  },

  computed: {
    // Format the animated number for display (e.g., add $ and commas)
    formattedNumber() {
      return (index) => {
        const value = this.animatedNumbers[index] || 0;
        // Format as currency (adjust locale if needed, e.g., 'en-US' for $)
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0
        }).format(value);
      };
    }
  },

  // Watch for changes in section data (optional, if data can update dynamically)
  watch: {
    section: {
      handler() {
        // Reset state and re-setup observer if section changes
        this.animatedNumbers = [];
        this.hasAnimated = false;
        this.$nextTick(() => {
          this.section.number_info_blocks.forEach((block, index) => {
            this.animatedNumbers[index] = 0;
          });
          this.setupIntersectionObserver();
        });
      },
      deep: true
    }
  },

  mounted() {
    // Initialize animated numbers to 0 (but don't start animation yet)
    this.section.number_info_blocks.forEach((block, index) => {
      this.animatedNumbers[index] = 0; ;
    });

    // Set up Intersection Observer to trigger animation on scroll
    this.setupIntersectionObserver();
  },

  beforeUnmount() {
    // Clean up the observer if the component is destroyed
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {
    stripBlockTags,

    // Parse the number from the block (e.g., extract 200000 from "$200,000")
    parseNumber(value) {
      if (typeof value === 'number')
        return value;
      if (typeof value === 'string') {
        // Remove non-numeric characters except decimal points
        const numeric = value.replace(/[^0-9.]/g, '');
        return parseFloat(numeric) || 0;
      }
      return 0; // Fallback
    },

    // Animate the number from 0 to target
    animateNumber(index, target) {
      const start = 0;
      const duration = this.animationDuration;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1); // 0 to 1

        // Easing function (ease-out for smoother end)
        const easedProgress = 1 - (1 - progress) ** 3;

        // Calculate current value
        const currentValue = Math.floor(start + (target - start) * easedProgress);
        this.animatedNumbers[index] = currentValue;

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
        else {
          // Ensure it ends exactly at target
          this.animatedNumbers[index] = target;
        }
      };

      requestAnimationFrame(animate);
    },

    // Set up Intersection Observer to detect when the section is in view
    setupIntersectionObserver() {
      const options = {
        root: null, // Use the viewport
        rootMargin: '0px',
        threshold: 0.7 // Trigger when 50% of the section is visible
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true; // Prevent re-triggering
            // Start animation for each block
            this.section.number_info_blocks.forEach((block, index) => {
              const target = this.parseNumber(block.number);
              this.animateNumber(index, target);
            });
            // Disconnect observer after triggering
            this.observer.disconnect();
          }
        });
      }, options);

      // Observe the section element
      if (this.$refs.countingSection) {
        this.observer.observe(this.$refs.countingSection);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Your existing styles remain unchanged
.counting-numbers-section {
  padding: 0;

  .container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    @media screen and (min-width: $breakpoint-md) {
      flex-wrap: nowrap;
    }
  }

  @media screen and (min-width: $breakpoint-md) {
    padding: 0;
  }

  h2::v-deep {
    font-size: 48px;
    line-height: 48px;
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    margin: 0;
    margin-bottom: 30px !important;

    @media screen and (min-width: $breakpoint-md) {
      font-size: 80px;
      line-height: 80px;
      margin-bottom: 0 !important;
    }

    strong {
      color: $thd-orange;
    }
  }

  .counting-numbers {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;

    @media screen and (min-width: $breakpoint-md) {
      flex-direction: row;
    }

    .blocks {
      .title {
        color: $thd-orange;
        font-size: 32px;
        font-weight: 700;

        p {
          margin-top: 0;
          margin-bottom: 10px;
        }
      }

      .content {
        color: $thd-black;
        line-height: normal;
      }

      .number-header {
        color: $thd-orange;
        font-size: 21px;
        font-weight: 700;
      }

      .number {
        color: $thd-orange;
        font-size: 72px;
        font-weight: 700;
        margin: 0;
      }

      .grants {
        font-size: 21px;
        font-weight: 700;
        margin: 0;
      }
    }
  }
}
</style>
